import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const TOC = [
  { id: 'sobre',           label: '1. Sobre o serviço' },
  { id: 'elegibilidade',   label: '2. Elegibilidade e maioridade' },
  { id: 'cadastro',        label: '3. Cadastro e segurança da conta' },
  { id: 'comunicacao',     label: '4. Preferências de comunicação' },
  { id: 'planos',          label: '5. Planos, pagamentos e renovação automática' },
  { id: 'garantia',        label: '6. Garantia de 7 dias (Direito de Arrependimento)' },
  { id: 'nao-reembolsavel',label: '7. Conteúdo digital não reembolsável' },
  { id: 'creditos',        label: '8. Créditos avulsos' },
  { id: 'cancelamento',    label: '9. Cancelamento de assinatura' },
  { id: 'ia',              label: '10. Conteúdo gerado por inteligência artificial' },
  { id: 'uso',             label: '11. Uso aceitável' },
  { id: 'pi',              label: '12. Propriedade intelectual' },
  { id: 'responsabilidade',label: '13. Limitação de responsabilidade' },
  { id: 'suspensao',       label: '14. Suspensão e encerramento de conta' },
  { id: 'alteracoes',      label: '15. Alterações nos Termos e nos preços' },
  { id: 'fora-brasil',     label: '16. Usuários fora do Brasil' },
  { id: 'lei',             label: '17. Lei aplicável e resolução de conflitos' },
  { id: 'contato',         label: '18. Contato' },
];

export function Terms() {
  const [publishedAt, setPublishedAt] = useState<string | null>(null);
  const [dbHtml,      setDbHtml]      = useState<string | null>(null);

  useEffect(() => {
    supabase
      .from('legal_documents')
      .select('content_html, published_at')
      .eq('doc_type', 'terms')
      .eq('is_current', true)
      .maybeSingle()
      .then(({ data }) => {
        if (data) { setPublishedAt(data.published_at); setDbHtml(data.content_html); }
      });
  }, []);

  const lastUpdated = publishedAt
    ? new Date(publishedAt).toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })
    : '22 de abril de 2026';

  return (
    <>
      <Helmet>
        <title>Termos de Uso, Professya</title>
        <meta name="description" content="Leia os Termos de Uso do Professya: elegibilidade, planos, garantia de 7 dias, créditos, IA e responsabilidades." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://professya.com/termos" />
      </Helmet>
      <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-base)', color: 'var(--text-primary)', padding: '2rem 1.5rem' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>

          <Link to="/" style={{ color: 'var(--petrol)', textDecoration: 'none', fontSize: 'var(--text-label)', display: 'inline-block', marginBottom: '2rem' }}>
            ← Voltar ao início
          </Link>

          <h1 className="font-display" style={{ fontSize: 'clamp(1.5rem,4vw,2.25rem)', marginBottom: '0.5rem' }}>
            Termos de Uso
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-label)', marginBottom: '2rem' }}>
            Última atualização: {lastUpdated}
          </p>

          {dbHtml ? (
            <div dangerouslySetInnerHTML={{ __html: dbHtml }} />
          ) : (<>

          {/* ToC */}
          <div className="card" style={{ marginBottom: '2rem' }}>
            <p style={{ fontWeight: 600, marginBottom: '0.75rem' }}>Índice</p>
            <ol style={{ paddingLeft: '1.25rem', margin: 0, lineHeight: 2 }}>
              {TOC.map(s => (
                <li key={s.id}>
                  <a href={`#${s.id}`} style={{ color: 'var(--petrol)', fontSize: 'var(--text-label)' }}>{s.label}</a>
                </li>
              ))}
            </ol>
          </div>

          <Section id="sobre" title="1. Sobre o serviço">
            <p>
              O Professya é uma plataforma digital de entretenimento e autoconhecimento baseada em astrologia,
              numerologia, tarô e práticas esotéricas similares.
            </p>
            <p style={{ marginTop: '0.75rem' }}>
              <strong>Os conteúdos gerados pelo Professya têm finalidade exclusivamente recreativa e de reflexão
              pessoal. Não constituem, e não devem ser interpretados como, aconselhamento médico, psicológico,
              financeiro, jurídico, espiritual ou de qualquer outra natureza profissional.</strong> Decisões
              importantes devem sempre ser tomadas com o auxílio de profissionais habilitados nas respectivas áreas.
            </p>
            <p style={{ marginTop: '0.75rem' }}>Ao utilizar o Professya, você reconhece e concorda que:</p>
            <ul style={{ paddingLeft: '1.25rem', lineHeight: 2, marginTop: '0.5rem' }}>
              <li>As leituras, previsões e interpretações fornecidas são baseadas em sistemas simbólicos e não possuem comprovação científica</li>
              <li>Todo o conteúdo entregue pela plataforma é gerado por inteligência artificial, não por astrólogos, tarólogos ou outros profissionais humanos</li>
              <li>Os resultados têm caráter subjetivo e podem variar</li>
            </ul>
          </Section>

          <Section id="elegibilidade" title="2. Elegibilidade e maioridade">
            <p>O Professya é destinado exclusivamente a pessoas com <strong>18 anos ou mais</strong>.</p>
            <Sub>2.1 Verificação de maioridade</Sub>
            <p>A verificação de idade ocorre em dois momentos obrigatórios:</p>
            <ul style={{ paddingLeft: '1.25rem', lineHeight: 2, marginTop: '0.5rem' }}>
              <li><strong>No cadastro:</strong> a data de nascimento é coletada e a maioridade verificada antes da criação da conta. Usuários com menos de 18 anos não podem criar conta.</li>
              <li><strong>Em atualizações de data de nascimento:</strong> qualquer alteração passa por nova verificação automática. Não é possível salvar uma data que indique menoridade.</li>
            </ul>
            <Sub>2.2 Declaração de maioridade</Sub>
            <p>Ao criar uma conta, você declara:</p>
            <ul style={{ paddingLeft: '1.25rem', lineHeight: 2, marginTop: '0.5rem' }}>
              <li>Ter 18 anos ou mais</li>
              <li>Ter capacidade legal para aceitar estes Termos e celebrar contratos digitais</li>
              <li>Agir em seu próprio nome</li>
            </ul>
            <Sub>2.3 Conta criada com data de nascimento falsa</Sub>
            <p>
              O fornecimento de data de nascimento incorreta para burlar a restrição de idade constitui violação
              contratual. O Professya reserva-se o direito de encerrar a conta sem reembolso. Contas de menores
              de idade serão encerradas e seus dados excluídos assim que identificadas.
            </p>
          </Section>

          <Section id="cadastro" title="3. Cadastro e segurança da conta">
            <ul style={{ paddingLeft: '1.25rem', lineHeight: 2 }}>
              <li>Você é responsável por manter a confidencialidade da sua senha e por todas as atividades realizadas com sua conta</li>
              <li>Cada endereço de e-mail pode estar associado a apenas uma conta</li>
              <li>É proibida a venda, transferência, compartilhamento ou cessão de contas a terceiros</li>
              <li>Ao identificar qualquer uso não autorizado da sua conta, notifique imediatamente pelo e-mail da seção 18</li>
              <li>O Professya nunca solicita sua senha por e-mail, chat ou qualquer outro canal</li>
            </ul>
          </Section>

          <Section id="comunicacao" title="4. Preferências de comunicação">
            <p>No momento do cadastro e a qualquer momento nas configurações do seu perfil, você pode escolher:</p>
            <ul style={{ paddingLeft: '1.25rem', lineHeight: 2, marginTop: '0.5rem' }}>
              <li><strong>E-mails de marketing:</strong> novidades, promoções e conteúdo editorial do Professya</li>
              <li><strong>Notificações e alertas da plataforma:</strong> lembretes de previsões, alertas de trânsitos relevantes, novos serviços disponíveis</li>
            </ul>
            <p style={{ marginTop: '0.75rem' }}>
              Cada tipo de comunicação possui um controle independente, alterável a qualquer momento em <strong>Perfil → Notificações</strong>.
            </p>
            <p style={{ marginTop: '0.75rem' }}>
              E-mails transacionais (recibos, confirmações de pagamento, alertas de segurança) são enviados independentemente das suas preferências de marketing, pois são necessários para a prestação do serviço.
            </p>
          </Section>

          <Section id="planos" title="5. Planos, pagamentos e renovação automática">
            <Sub>5.1 Planos disponíveis</Sub>
            <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-label)' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    {['Plano', 'Descrição', 'Cobrança'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '0.5rem', color: 'var(--text-secondary)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Gratuito',        'Acesso limitado a funcionalidades básicas',          'Sem custo, sem cartão'],
                    ['Mensal',          'Acesso completo à plataforma',                       'Mensal, renovação automática'],
                    ['Anual',           'Acesso completo com desconto',                       'Anual, renovação automática'],
                    ['Créditos Avulsos','Compra pontual de créditos para serviços específicos','Cobrança única'],
                  ].map(([plano, desc, cob]) => (
                    <tr key={plano} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '0.5rem', color: 'var(--text-primary)', fontWeight: 600 }}>{plano}</td>
                      <td style={{ padding: '0.5rem', color: 'var(--text-secondary)' }}>{desc}</td>
                      <td style={{ padding: '0.5rem', color: 'var(--text-muted)' }}>{cob}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Sub>5.2 Preços e impostos</Sub>
            <p>Os preços são exibidos na moeda correspondente à sua localização. Todos os valores indicados incluem os impostos aplicáveis conforme a legislação vigente.</p>
            <Sub>5.3 Renovação automática</Sub>
            <p>Planos com renovação automática serão cobrados no método de pagamento cadastrado ao final de cada período. Você receberá um aviso por e-mail <strong>7 dias antes</strong> de cada renovação. Você pode cancelar a renovação automática a qualquer momento até 24 horas antes da data de cobrança.</p>
            <Sub>5.4 Falha no pagamento</Sub>
            <p>Em caso de falha, o Professya tentará processar a cobrança por até 3 dias consecutivos. Após esse período sem sucesso, o acesso será reduzido ao plano Gratuito. Seus dados e histórico serão mantidos por 30 dias para reativação.</p>
            <Sub>5.5 Alterações de preço</Sub>
            <p>Alterações nos preços de assinaturas ativas serão comunicadas com 30 dias de antecedência. Se você não concordar com o novo preço, poderá cancelar antes da data de vigência sem qualquer cobrança adicional.</p>
          </Section>

          <Section id="garantia" title="6. Garantia de 7 dias (Direito de Arrependimento)">
            <p>
              Em conformidade com o Art. 49 do CDC, você tem o direito de cancelar sua assinatura em até
              <strong> 7 dias corridos</strong> a partir da data da compra, com reembolso integral do valor pago,
              desde que nenhum serviço de geração de conteúdo personalizado tenha sido utilizado no período.
            </p>
            <p style={{ marginTop: '0.75rem' }}>
              Se serviços foram utilizados dentro dos 7 dias, o reembolso será calculado descontando o valor
              de referência dos serviços consumidos. Se o valor consumido for igual ou superior ao da assinatura,
              não haverá reembolso.
            </p>
            <p style={{ marginTop: '0.75rem' }}>Como solicitar:</p>
            <ul style={{ paddingLeft: '1.25rem', lineHeight: 2, marginTop: '0.5rem' }}>
              <li>Por e-mail: <strong>suporte@professya.com</strong> com o assunto "Cancelamento CDC Art. 49"</li>
              <li>Pelo aplicativo: Perfil → Assinatura → Cancelar (dentro do prazo de 7 dias)</li>
            </ul>
            <p style={{ marginTop: '0.75rem' }}>
              O reembolso será processado em até <strong>10 dias úteis</strong> pelo mesmo método de pagamento utilizado na compra.
            </p>
          </Section>

          <Section id="nao-reembolsavel" title="7. Conteúdo digital não reembolsável">
            <p>
              Determinados serviços envolvem a geração de conteúdo digital personalizado e sob demanda, entregue
              imediatamente no ato da solicitação. Por sua natureza, este conteúdo não pode ser "devolvido" após
              a geração.
            </p>
            <Sub>7.1 Serviços com entrega imediata e não reembolsáveis</Sub>
            <p>
              Esta categoria inclui: Mapa Astral, Sinastria, AstroMatch, Tarot de Marselha, Tarot Cigano,
              Numerologia, Rota Profissional, Eclipse Insight, Astrocartografia, Human Design, Astrologia Védica,
              I Ching, Radar Retrógrado, Astro Coach, Previsão Mensal Personalizada, Previsão Anual Personalizada
              e qualquer outro serviço que gere análise personalizada sob demanda.
            </p>
            <Sub>7.2 Consentimento prévio obrigatório</Sub>
            <p>Antes de gerar qualquer serviço desta categoria, o sistema exibirá a seguinte confirmação:</p>
            <div style={{ backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-card)', padding: '0.875rem 1rem', margin: '0.75rem 0', fontStyle: 'italic', color: 'var(--text-secondary)' }}>
              "Estou solicitando a entrega imediata de conteúdo digital personalizado gerado especificamente para mim. Compreendo que, após a geração, este serviço não poderá ser reembolsado, conforme Art. 49, parágrafo único do CDC. Confirmo minha solicitação."
            </div>
            <p>Este consentimento é registrado com data, hora e identificação da conta.</p>
            <Sub>7.3 Serviços recorrentes</Sub>
            <p>
              Serviços de conteúdo recorrente, como previsões diárias, semanais, mensais e anuais, são
              considerados entregues proporcionalmente ao período de uso. Em caso de cancelamento após os 7 dias
              de arrependimento, não há reembolso do período já pago, mas o acesso é mantido até o vencimento.
            </p>
          </Section>

          <Section id="creditos" title="8. Créditos avulsos">
            <ul style={{ paddingLeft: '1.25rem', lineHeight: 2 }}>
              <li>Créditos são consumidos no momento em que você solicita a geração de leituras avulsas</li>
              <li>Créditos utilizados não são reembolsáveis, exceto em casos de falha técnica documentada atribuível ao Professya</li>
              <li>Créditos não possuem data de expiração enquanto sua conta estiver ativa</li>
              <li>Créditos não são transferíveis entre contas</li>
              <li>Em caso de encerramento definitivo do serviço, créditos não utilizados serão reembolsados proporcionalmente, mediante aviso prévio de no mínimo 30 dias</li>
            </ul>
          </Section>

          <Section id="cancelamento" title="9. Cancelamento de assinatura">
            <p>Você pode cancelar sua assinatura a qualquer momento:</p>
            <ul style={{ paddingLeft: '1.25rem', lineHeight: 2, marginTop: '0.5rem' }}>
              <li>No aplicativo: <strong>Perfil → Assinatura → Cancelar</strong></li>
              <li>No iOS: Ajustes → [seu nome] → Assinaturas → Professya → Cancelar</li>
              <li>No Android: Google Play → Assinaturas → Professya → Cancelar</li>
              <li>Por e-mail: suporte@professya.com</li>
            </ul>
            <p style={{ marginTop: '0.75rem' }}>
              O cancelamento entra em vigor no final do período já pago. Você mantém acesso completo até a data de vencimento.
              Não há cobrança de multa ou taxa por cancelamento antecipado.
            </p>
          </Section>

          <Section id="ia" title="10. Conteúdo gerado por inteligência artificial">
            <Sub>10.1 Natureza do conteúdo</Sub>
            <p>
              Todo o conteúdo personalizado entregue pelo Professya é gerado por sistemas de inteligência
              artificial generativa. Você está ciente de que está interagindo com conteúdo criado por IA,
              não por profissionais humanos.
            </p>
            <Sub>10.2 Seus dados e treinamento de IA</Sub>
            <p>
              Seus dados pessoais não são utilizados para treinar modelos de inteligência artificial, seja
              pelo Professya ou por seus provedores de tecnologia. Esta restrição está contratualmente estabelecida.
            </p>
            <Sub>10.3 Licença de uso do conteúdo gerado</Sub>
            <p>
              As leituras geradas especificamente para você são licenciadas para seu uso pessoal e não comercial.
              Você pode ler, salvar e compartilhá-las para uso pessoal, inclusive em redes sociais, desde que
              mantenha a referência ao Professya. É proibido utilizar as leituras para fins comerciais ou
              apresentá-las como geradas por profissional humano.
            </p>
          </Section>

          <Section id="uso" title="11. Uso aceitável">
            <p>É expressamente proibido:</p>
            <ul style={{ paddingLeft: '1.25rem', lineHeight: 2, marginTop: '0.5rem' }}>
              <li>Utilizar o Professya para fins fraudulentos, ilegais ou que prejudiquem terceiros</li>
              <li>Realizar engenharia reversa, decompilação ou tentativa de acesso não autorizado ao código-fonte ou sistemas internos</li>
              <li>Criar contas automatizadas, usar scripts ou bots para acessar a plataforma em escala</li>
              <li>Fornecer data de nascimento falsa para contornar a restrição de maioridade</li>
              <li>Revender, sublicenciar ou monetizar conteúdos gerados pela plataforma sem autorização escrita prévia</li>
              <li>Tentar contornar mecanismos de pagamento, limitações de plano ou controles de acesso</li>
            </ul>
            <p style={{ marginTop: '0.75rem' }}>O descumprimento pode resultar na suspensão ou encerramento imediato da conta, conforme seção 14.</p>
          </Section>

          <Section id="pi" title="12. Propriedade intelectual">
            <p>
              Todos os elementos da plataforma Professya, incluindo textos, interfaces, marca, design,
              metodologia, algoritmos e sistemas de IA, são protegidos pelas leis brasileiras e internacionais
              de propriedade intelectual.
            </p>
          </Section>

          <Section id="responsabilidade" title="13. Limitação de responsabilidade">
            <p>
              O Professya não se responsabiliza por decisões tomadas com base em leituras, previsões ou
              qualquer outro conteúdo da plataforma, dada a natureza simbólica, subjetiva e não científica
              dos serviços oferecidos.
            </p>
            <p style={{ marginTop: '0.75rem' }}>
              O serviço é fornecido "no estado em que se encontra". O Professya empenha melhores esforços
              para manter disponibilidade acima de 99,5% ao mês. A responsabilidade total do Professya por
              qualquer reclamação não excederá o valor pago pelo usuário nos últimos 3 meses de assinatura.
            </p>
          </Section>

          <Section id="suspensao" title="14. Suspensão e encerramento de conta">
            <Sub>14.1 Encerramento voluntário e exclusão de dados</Sub>
            <p>
              Você pode solicitar o encerramento da sua conta a qualquer momento pelo aplicativo
              (<strong>Perfil → Privacidade → Excluir minha conta</strong>) ou por e-mail para suporte@professya.com.
              Todos os seus dados pessoais serão excluídos ou anonimizados no prazo de 30 dias, com exceção
              dos dados mantidos por obrigação legal. Você receberá confirmação por e-mail quando o processo for concluído.
            </p>
            <Sub>14.2 Suspensão por violação dos Termos</Sub>
            <ul style={{ paddingLeft: '1.25rem', lineHeight: 2, marginTop: '0.5rem' }}>
              <li><strong>Casos não urgentes:</strong> aviso prévio de 7 dias com descrição da violação e oportunidade de contestação</li>
              <li><strong>Casos urgentes (fraude, risco à segurança):</strong> suspensão imediata, com notificação posterior</li>
            </ul>
            <Sub>14.3 Suspensão por menoridade identificada</Sub>
            <p>
              Se uma conta for identificada como pertencente a menor de idade após o cadastro, a conta será
              suspensa imediatamente. O usuário terá 15 dias para contestar com comprovação de maioridade.
              Sem contestação no prazo, a conta e todos os dados são excluídos.
            </p>
          </Section>

          <Section id="alteracoes" title="15. Alterações nos Termos e nos preços">
            <p>
              O Professya pode atualizar estes Termos a qualquer momento. Alterações significativas serão
              comunicadas por e-mail com <strong>30 dias de antecedência</strong>. O uso continuado da
              plataforma após a data de vigência implica aceitação dos novos Termos.
            </p>
          </Section>

          <Section id="fora-brasil" title="16. Usuários fora do Brasil">
            <p>
              Para usuários residentes na União Europeia, a eleição de foro no Brasil não prejudica o direito
              de acionar as autoridades de proteção de dados locais nem os direitos garantidos pela legislação
              de defesa do consumidor do país de residência. As disposições obrigatórias de proteção ao consumidor
              do país de residência prevalecem sobre a eleição de foro em caso de conflito.
            </p>
          </Section>

          <Section id="lei" title="17. Lei aplicável e resolução de conflitos">
            <p>
              Estes Termos são regidos pelas leis da República Federativa do Brasil. Eventuais conflitos
              serão submetidos ao foro da Comarca de São Paulo, SP, ressalvado o direito do consumidor
              de optar pelo foro do seu domicílio conforme o Art. 101, I do CDC.
            </p>
            <p style={{ marginTop: '0.75rem' }}>
              Antes de qualquer medida judicial, as partes comprometem-se a buscar solução amigável pelo
              e-mail <strong>suporte@professya.com</strong>.
            </p>
          </Section>

          <Section id="contato" title="18. Contato">
            <ul style={{ paddingLeft: '1.25rem', lineHeight: 2 }}>
              <li><strong>E-mail:</strong> suporte@professya.com</li>
              <li><strong>Site:</strong> professya.com</li>
            </ul>
            <p style={{ marginTop: '0.75rem' }}>Respondemos em até 5 dias úteis para questões relacionadas a estes Termos.</p>
          </Section>

          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: '1.5rem', marginTop: '2rem', color: 'var(--text-muted)', fontSize: 'var(--text-caption)', lineHeight: 1.8 }}>
            <p>Professya, Segredo Astral | professya.com</p>
            <p style={{ marginTop: '0.5rem' }}>
              <Link to="/privacy" style={{ color: 'var(--petrol)' }}>Política de Privacidade</Link>
              {' · '}
              <Link to="/cancellation" style={{ color: 'var(--petrol)' }}>Cancelamento</Link>
              {' · '}
              <Link to="/" style={{ color: 'var(--petrol)' }}>Voltar ao início</Link>
            </p>
          </div>

          </>)}
        </div>
      </div>
    </>
  );
}

function Section({ id, title, children }: { id?: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} style={{ marginBottom: '2rem' }}>
      <h2 className="font-display" style={{ fontSize: '1.125rem', marginBottom: '0.875rem', color: 'var(--text-primary)' }}>{title}</h2>
      <div style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>{children}</div>
    </section>
  );
}

function Sub({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '1rem 0 0.35rem' }}>{children}</p>
  );
}
